<script setup lang="ts">
/**
 * The balance at the top of the navbar's panels (NavAccount's and
 * NavBilling's), Tencent Cloud's: on a tint of the primary, the eye that
 * hides it, 充值 beside it, and under a rule what this month cost and when
 * the figures were read. A sum not read yet is a dash.
 */
import { useI18n } from 'vue-i18n'
import { IconEye, IconEyeInvisible } from '@arco-design/web-vue/es/icon'
import { balanceHidden, shown, toggleBalanceHidden } from '@/utils/wallet'

defineProps<{ balanceCents?: number; spentCents?: number; readAt: string }>()
defineEmits<{ topUp: [] }>()

const { t } = useI18n()
</script>

<template>
  <div class="wallet">
    <div class="wallet-main">
      <div>
        <div class="wallet-label">
          {{ t('navbar.balance') }}
          <button
            type="button"
            class="eye"
            :aria-label="balanceHidden ? t('navbar.showBalance') : t('navbar.hideBalance')"
            @click="toggleBalanceHidden"
          >
            <IconEyeInvisible v-if="balanceHidden" />
            <IconEye v-else />
          </button>
        </div>
        <div class="wallet-amount">{{ shown(balanceCents) }}</div>
      </div>
      <a-button type="primary" @click="$emit('topUp')">{{ t('navbar.topUp') }}</a-button>
    </div>
    <div class="wallet-foot">
      <span>{{ t('navbar.monthSpent', { n: shown(spentCents) }) }}</span>
      <span>{{ t('navbar.updatedAt', { t: readAt || '—' }) }}</span>
    </div>
  </div>
</template>

<style scoped>
/* --nav-rule and --nav-tint come from the panel (.nav-panel, base.css). */
.wallet {
  padding: 16px;
  background: var(--nav-tint);
}
.wallet-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--nav-rule);
}
.wallet-label {
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-3);
  font-size: 13px;
  line-height: 20px;
}
.eye {
  display: flex;
  padding: 2px;
  border: none;
  background: none;
  color: var(--color-text-3);
  font-size: 15px;
  cursor: pointer;
}
.eye:hover {
  color: var(--color-text-1);
}
.wallet-amount {
  margin-top: 4px;
  color: var(--color-text-1);
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  font-variant-numeric: tabular-nums;
}
.wallet-foot {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding-top: 12px;
  color: var(--color-text-3);
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
</style>
